import React from 'react'
import { cn, shortenNumber } from '@/lib/utils'

import { Progress } from '@/components/ui'

interface SalesChartProps extends React.ComponentPropsWithoutRef<'div'> {
  data?: any
}

export const SalesChart = ({ data }: SalesChartProps) => {
  const chart = React.useMemo(
    () => [
      {
        label: 'Today',
        value: Number(data?.payments_today?.transaction_total) || 0,
      },
      {
        label: 'Yesterday',
        value: Number(data?.payments_yesterday?.transaction_total) || 0,
      },
      {
        label: 'This Week',
        value: Number(data?.payments_this_week?.transaction_total) || 0,
      },
      {
        label: 'This Month',
        value: Number(data?.payments_this_month?.transaction_total) || 0,
      },
    ],
    [data],
  )

  const { total, highest, distributed } = React.useMemo(
    () => processData(chart),
    [chart],
  )

  return (
    <div className="relative flex w-full flex-col gap-3 rounded-lg border border-gray-100 px-6 pb-14 pt-6 shadow-card dark:border-gray-700">
      {chart.map((item, index) => (
        <DataItem key={item.label} {...item} total={total} index={index} />
      ))}

      {/* Show vertical lines */}
      <div className="absolute inset-0 z-0 flex w-full justify-evenly text-gray-400">
        <div className="absolute bottom-6 left-6 top-0 flex h-full w-4 flex-col justify-end pb-4">
          <span className="font-mono text-sm font-medium">0</span>
        </div>

        {distributed.map((value, index) => (
          <VerticalLine
            key={value}
            value={value}
            index={index}
            dataLength={data.length}
          />
        ))}

        <div className="absolute bottom-6 right-6 top-0 flex h-full flex-col justify-end pb-4">
          <span className="font-mono text-sm font-medium">
            {shortenNumber(highest)}
          </span>
        </div>
      </div>
    </div>
  )
}

type DataItemProps = {
  label: string
  value: number
  total: number
  index: number
}

const DataItem = ({ label, value, total, index }: DataItemProps) => {
  const progress = (value / total) * 100
  return (
    <div key={label} className="relative z-10 flex w-full flex-col gap-2">
      <div className="flex w-full items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <span className="font-mono text-sm font-medium">
          {shortenNumber(value)}
        </span>
      </div>

      <div className="flex w-full">
        <Progress
          value={progress}
          className={cn({
            '[&>div]:bg-blue-700': index === 0,
          })}
        />
      </div>
    </div>
  )
}

type VerticalLineProps = {
  value: number
  index: number
  dataLength: number
}

const VerticalLine = ({ value, index, dataLength }: VerticalLineProps) => (
  <div
    className="relative flex h-full w-4 flex-col items-center pb-4"
    key={value}
  >
    <div
      className={cn(
        'h-full w-px border-r border-dashed border-gray-100 dark:border-gray-700',
        index !== dataLength - 1 && 'mr-2',
      )}
    />
    <span className="font-mono text-sm font-medium">
      {shortenNumber(value)}
    </span>
  </div>
)

/**
 * Processes the data array to calculate total, highest value, and evenly distributed values.
 * @param data - The data array containing label-value pairs.
 * @returns An object containing the total, highest value, and evenly distributed values.
 */
const processData = (
  data: {
    label: string
    value: number
  }[],
) => {
  const total = data.reduce((acc, { value }) => acc + value, 0)
  const highest = total
  const distributed = [highest / 4, highest / 2, (highest / 4) * 3]
  return { total, highest, distributed }
}
